import dayjs from "dayjs";
import firebase from "firebase/compat/app";
import { Campaign, Client, GrailDoc, List, Pitch, Talent, User, type AirtableOperationStatsHourlyDoc, type AirtableSyncOutstandingSnapshotDoc } from '../../shared/types';
import { timestampOrDateToDate } from "../../utils/dates";
import { firestore, logDatabaseReads } from "./app";

function hourStartToTimeMs(doc: AirtableOperationStatsHourlyDoc): number {
    return (timestampOrDateToDate(doc.hourStart, false) as Date).getTime();
}

const AIRTABLE_OPERATION_STATS_HOURLY_COLLECTION = "airtableOperationStatsHourly";
const AIRTABLE_SYNC_OUTSTANDING_SNAPSHOT_COLLECTION = "airtableSyncOutstandingSnapshot";
const AIRTABLE_SYNC_OUTSTANDING_SNAPSHOT_DOC_ID = "latest";

/** Last N days of hourly rollup rows (by `hourStart`). Requires Firestore rule allowing admin read on this collection. */
export async function fetchAirtableOperationStatsHourly(since: Date): Promise<AirtableOperationStatsHourlyDoc[]> {
    const snap = await firestore
        .collection(AIRTABLE_OPERATION_STATS_HOURLY_COLLECTION)
        .where("hourStart", ">=", firebase.firestore.Timestamp.fromDate(since))
        .get();
    logDatabaseReads("fetchAirtableOperationStatsHourly", snap.docs.length);
    const docs = snap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
    })) as AirtableOperationStatsHourlyDoc[];
    docs.sort((a, b) => hourStartToTimeMs(a) - hourStartToTimeMs(b));
    return docs;
}

/** Latest outstanding sync snapshot (hourly job). Missing doc → null (page renders empty safely). */
export async function fetchAirtableSyncOutstandingSnapshot(): Promise<AirtableSyncOutstandingSnapshotDoc | null> {
    const snap = await firestore
        .collection(AIRTABLE_SYNC_OUTSTANDING_SNAPSHOT_COLLECTION)
        .doc(AIRTABLE_SYNC_OUTSTANDING_SNAPSHOT_DOC_ID)
        .get();
    logDatabaseReads("fetchAirtableSyncOutstandingSnapshot", snap.exists ? 1 : 0);
    if (!snap.exists) {
        return null;
    }
    return snap.data() as AirtableSyncOutstandingSnapshotDoc;
}

////////////////////
// Firebase Queries:
////////////////////
// A primary document is the doc a page is scoped to, loaded with `get*ById` or a live snapshot (use*ById).
// Always fetch that document. Do not use a `useFresh*` collection as source of truth for it.
// Mix a primary-document read with `useFresh*` on the same page when secondary large reads are needed
// (e.g. Client selection for /edit-user).
// Full-page uses of `DataLoading` wait on the primary document, not on collections.

// Primary document (see Firebase Queries comment).
export async function getTalentById(talentId: string) {
    const talentRef = firestore.collection('talent').doc(talentId);
    const talentData = (await talentRef.get()).data();
    logDatabaseReads('getTalentById', 1);

    return {
        ...talentData,
        id: talentId,
    } as Talent;
}

export async function getCountries() {
    const docRef = firestore.collection("lookups").doc("countries");
    const docSnap = await docRef.get();
    logDatabaseReads('getCountries', 1);

    if (docSnap.exists) {
        const doc: any = docSnap.data();

        // Alpha sort, but put US and UK at front, then return
        let countries = doc.countries.sort();
        countries = countries.filter((country: string) => country !== 'United Kingdom' && country !== 'United States');
        countries.unshift('United Kingdom');
        countries.unshift('United States');
        return countries;
    } else {
        return null;
    }
}

export async function getContentTags() {
    const docRef = firestore.collection("lookups").doc("contentTags");
    const docSnap = await docRef.get();
    logDatabaseReads('getContentTags', 1);

    if (docSnap.exists) {
        const doc: any = docSnap.data();
        return doc.tags.sort();
    } else {
        return null;
    }
}

// export async function getListById(listId: string) {
//     const listRef = firestore.collection('lists').doc(listId);
//     const listData = (await listRef.get()).data();
//     logDatabaseReads('getListById', 1);

//     return {
//         ...listData,
//         id: listId,
//     } as List;
// }

export async function getActiveLists() {
    const res = await firestore.collection("lists").where("active", "==", true).get();
    logDatabaseReads("getActiveLists", res.docs.length);

    return res.docs.map((doc) => doc.data() as List);
}

export async function getCampaignsByBookerId(uid: string, onlyRecent = true, onlyUninvoiced = false) {
    const res = await firestore.collection("campaigns").where("booker.uid", "==", uid).get();
    logDatabaseReads("getCampaignsByBookerId", res.docs.length);

    const campaigns = res.docs.map((doc) => doc.data() as Campaign);
    let filteredCampaigns = campaigns;

    if (onlyRecent) {
        const daysRecent = dayjs().subtract(45, 'days').toDate();
        filteredCampaigns = filteredCampaigns.filter((campaign) => !campaign.airtableDuplicate && (campaign.docMeta?.createTime && campaign.docMeta?.createTime.toDate() > daysRecent));
    }
    if (onlyUninvoiced) {
        filteredCampaigns = filteredCampaigns.filter((campaign) => !campaign.invoiceDate);
    }
    return filteredCampaigns;
}

// Primary document (see Firebase Queries comment).
export async function getUserById(uid: string): Promise<User> {
    const userRef = firestore.collection('users').doc(uid);
    const userData = (await userRef.get()).data();
    logDatabaseReads("getUserById", 1);

    return {
        ...userData,
        id: uid,
    };
}

export async function getDocFromCollection(collection: string, docId: string): Promise<GrailDoc> {
    const docRef = firestore.collection(collection).doc(docId);
    const docData = (await docRef.get()).data();
    logDatabaseReads("getDocFromCollection", 1);

    return {
        ...docData,
        id: docId,
    };
}

// Primary document (see Firebase Queries comment).
export async function getClientById(clientId: string): Promise<Client> {
    const clientRef = firestore.collection('clients').doc(clientId);
    const clientData = (await clientRef.get()).data() as Client;
    logDatabaseReads("getClientById", 1);

    return {
        ...clientData,
        id: clientId,
    };
}

// Primary document (see Firebase Queries comment).
export async function getCampaignById(campaignId: string): Promise<Campaign> {
    const snapshot = await firestore.collection("campaigns").doc(campaignId).get();
    logDatabaseReads("getCampaignById", 1);
    if (!snapshot.exists) {
        throw new Error(`Campaign not found: ${campaignId}`);
    }

    return {
        ...(snapshot.data() as Campaign),
        id: campaignId,
    };
}

export async function getPitchesByCampaignId(campaignId: string): Promise<Pitch[]> {
    const res = await firestore.collection("pitches").where("campaignId", "==", campaignId).get();
    logDatabaseReads("getPitchesByCampaignId", res.docs.length);

    return res.docs.map((doc) => ({
        ...(doc.data() as Pitch),
        id: doc.id,
    }));
}
